import React, { useCallback, useEffect, useState } from "react";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_Tooltip } from "@danishagro/shared/src/components/atoms/Tooltip/Tooltip.component";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { clamp } from "@baggie/core";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import S from "./QuantityPicker.module.scss";

export interface DA_QuantityPickerProps {
    value: number;
    onChange: (newQuantity: number) => void;
    disabled?: boolean;
    compactOnMobile?: boolean;
    inputId?: string;
    higherMax?: boolean;
    minQty?: string;
    minQtyInfoText?: string;
}

export const DA_QuantityPicker = ({
    value,
    onChange,
    disabled,
    compactOnMobile,
    inputId,
    higherMax,
    minQty = "1",
    minQtyInfoText,
}: DA_QuantityPickerProps) => {
    const { isMobile } = useScreen();
    const { getDictionaryString } = useTranslations();
    const { showOverlay, closeOverlay } = useDynamicOverlay();

    const showButtons = !isMobile || !compactOnMobile;
    const higherMaxValue = higherMax ? 9999999 : 999;
    const minQtyValue = parseInt(minQty) || 1;

    // Ensure the displayed value never goes below minQtyValue
    const minValue = isNaN(value) || value < minQtyValue ? minQtyValue : value;
    const [inputValue, setInputValue] = useState<string>(String(minValue));

    // Sync input display
    useEffect(() => {
        setInputValue(String(minValue));
        if (value !== minValue) {
            onChange(minValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minValue]);

    const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
        // Select all text on focus for easy replacement
        event.target.select();
    }, []);

    const handleChange = useCallback(
        (newQuantity: number) =>
            onChange(clamp(newQuantity, { min: minQtyValue, max: higherMaxValue })),
        [onChange, minQtyValue, higherMaxValue]
    );

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    }, []);

    const openDynamicOverlay = useCallback(() => {
        showOverlay({
            id: "min_qty_open",
            title: getDictionaryString("minQtyOverlayTitle"),
            content: (
                <>
                    <DA_Text noPadding>
                        {getDictionaryString("minimumQuantityInfoTextOverlay", {
                            minQty: minQtyValue,
                        })}
                    </DA_Text>
                    <DA_Button
                        className={S.overlayButton}
                        title={getDictionaryString("minimumQuantityOverlayBtn")}
                        onClick={closeOverlay}
                    />
                </>
            ),
        });
    }, [showOverlay, closeOverlay, minQtyValue, getDictionaryString]);

    const handleBlur = useCallback(() => {
        const parsed = parseInt(inputValue);
        // Overlay to inform the user if they typed below the minimum
        if (compactOnMobile && isMobile && !isNaN(parsed) && parsed < minQtyValue) {
            openDynamicOverlay();
        }

        // If the input is invalid or below minimum, reset to minValue. If it's above maximum, clamp it.
        const clamped = clamp(!isNaN(parsed) ? parsed : minValue, {
            min: minQtyValue,
            max: higherMaxValue,
        });
        setInputValue(String(clamped));
        if (clamped !== value) onChange(clamped);
    }, [
        inputValue,
        value,
        onChange,
        minQtyValue,
        higherMaxValue,
        minValue,
        compactOnMobile,
        isMobile,
        openDynamicOverlay,
    ]);

    const minusBtn = (
        <DA_Button
            className={cn(S.button, S.minus)}
            onClick={() => handleChange(value <= minQtyValue ? minQtyValue : value - 1)}
            icon={DA_IconNames.Minus}
            disabled={value <= minQtyValue || disabled}
            title={getDictionaryString("accessibilityButtonMinus")}
            hideText
            ariaLabel={getDictionaryString("accessibilityButtonMinus")}
        />
    );

    return (
        <div className={cn(S.container, "swiper-no-swiping")}>
            {showButtons &&
                // Tooltip to inform the user if they typed below the minimum
                (value <= minQtyValue && minQtyValue > 1 ? (
                    <DA_Tooltip
                        placement="top"
                        text={
                            minQtyInfoText ??
                            getDictionaryString("minimumQuantityInfoTextTooltip", {
                                minQty: minQtyValue,
                            })
                        }
                    >
                        {minusBtn}
                    </DA_Tooltip>
                ) : (
                    minusBtn
                ))}

            <input
                id={inputId}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={cn(S.input, showButtons && S.sharpCorners, higherMax && S.maxValue)}
                pattern="[0-9]*"
                min={minQtyValue}
                max={higherMaxValue}
                maxLength={3}
                inputMode="numeric"
                type="number"
                onFocus={handleFocus}
                disabled={disabled}
                aria-label={getDictionaryString("accessibilityQuantityInputField")}
            />

            {showButtons && (
                <DA_Button
                    className={cn(S.button, S.add)}
                    onClick={() => handleChange(value + 1)}
                    icon={DA_IconNames.Add}
                    disabled={value >= higherMaxValue || disabled}
                    title={getDictionaryString("accessibilityButtonPlus")}
                    hideText
                    ariaLabel={getDictionaryString("accessibilityButtonPlus")}
                />
            )}
        </div>
    );
};
